import { useState, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import useAds from "@hooks/useAds"
import Navbar from "@components/Navbar/Navbar"
import { useNavigate } from "react-router"
import useAdCopies from "@hooks/useAdCopies"
import { extractDriveId } from "@components/SortableItem/helper"
import useDefaultValues from "@hooks/useDefaultValues"
import { notify } from "@utils/notify"
import { Loader } from "@components/Loader"
import { Copy, Clipboard, Save } from "lucide-react"
import { fetchAllAdTemplates, saveAdTemplate } from "@services/adTemplates";
import { Box, Button, Modal } from "@mui/material"

//const userId = import.meta.env.VITE_USER_ID

const AdCopyPage = () => {
  const userId = localStorage.getItem("userId")
  const storedAdAccountId = localStorage.getItem("adAccountId")
  const storedAdAccountName = localStorage.getItem("adAccountName")

  const [isSaving, setIsSaving] = useState(false)
  const [editedCopies, setEditedCopies] = useState({})
  const [adTemplates, setAdTemplates] = useState([]);
  const [previewImage, setPreviewImage] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [copiedAdData, setCopiedAdData] = useState(null)
  const [headlines, setHeadlines] = useState(['']);
  const [primaryTexts, setPrimaryTexts] = useState(['']);
  const { handleGetAdsWithCreatives } = useAds()
  const [adTemplatesModalOpen, setAdTemplatesModalOpen] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("")
  const { handleGetAdCopies, handleUpdateAdCopy } = useAdCopies()
  const navigate = useNavigate()
  const { handleGetDefaultValues } = useDefaultValues()

  const { ads } = useSelector((state) => state.ads)
  const { adCopies: adCopiesState } = useSelector((state) => state.adCopies)
  const { defaultValues } = useSelector((state) => state.defaultValues)

  const [selectedAdForTemplate, setSelectedAdForTemplate] = useState(null);

  const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState("");
  const [adForSaveTemplate, setAdForSaveTemplate] = useState(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  const handleLoadAdSetupClick = async (adId) => {
    console.log("Load Ad Setup clicked, adId:", adId);
    try {
      const response = await fetchAllAdTemplates({ user_id: userId });
      if (response?.data?.templates) {
        setAdTemplates(response.data.templates);
      }
      setTemplateSearchQuery("")
      setSelectedAdForTemplate(adId);
      setAdTemplatesModalOpen(true);
    } catch (error) {
      console.error("Error loading ad templates:", error);
      notify("Failed to load ad templates", "error");
    }
  };

  const filteredAdTemplates = useMemo(() => {
    const query = templateSearchQuery.trim().toLowerCase()
    if (!query) return adTemplates

    return adTemplates.filter((template) => {
      const templateName = (
        template.templateName ||
        template.template_name ||
        ""
      ).toLowerCase()
      if (templateName.includes(query)) return true

      const creatorName = (template.createdBy?.name || "").toLowerCase()
      if (creatorName.includes(query)) return true

      const headlines = template.headlines || []
      if (headlines.some((h) => (h || "").toLowerCase().includes(query)))
        return true

      const primaryTexts = template.primary_texts || template.primaryTexts || []
      if (primaryTexts.some((t) => (t || "").toLowerCase().includes(query)))
        return true

      return false
    })
  }, [adTemplates, templateSearchQuery])

  const handleBulkPaste = (adId, field, pastedText, maxFields = 5) => {
    const values = []
    const lines = pastedText.split("\n")
    let currentValue = ""
    let inQuotes = false

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()

      if (line.startsWith('"') && !inQuotes) {
        inQuotes = true
        currentValue = line.substring(1)
      } else if (line.endsWith('"') && inQuotes) {
        currentValue += (currentValue ? "\n" : "") + line.slice(0, -1)
        currentValue = currentValue.replace(/""/g, '"')
        values.push(currentValue)
        currentValue = ""
        inQuotes = false
      } else if (inQuotes) {
        currentValue += (currentValue ? "\n" : "") + line
      } else if (line.length > 0) {
        values.push(line.replace(/""/g, '"'))
      }
    }

    if (inQuotes && currentValue) {
      values.push(currentValue.replace(/""/g, '"'))
    }

    if (values.length === 0) return

    const copies = getAdCopyForCreative(adId)
    const availableSlots = Math.min(values.length, maxFields, copies.length)

    setEditedCopies((prev) => {
      const newState = { ...prev }

      for (let i = 0; i < availableSlots; i++) {
        const key = `${adId}-${i}`
        newState[key] = {
          ...newState[key],
          adId,
          [field]: values[i],
          index: i,
        }
      }

      return newState
    })
  }

  const handleHeadlinePaste = (adId, index, e) => {
    if (index !== 0) {
      return
    }

    const copies = getAdCopyForCreative(adId)
    if (copies.length <= 1) {
      return
    }

    e.preventDefault()
    const pastedText = e.clipboardData.getData("text")
    handleBulkPaste(adId, "headline", pastedText)
  }

  const handlePrimaryTextPaste = (adId, index, e) => {
    if (index !== 0) {
      return
    }

    const copies = getAdCopyForCreative(adId)
    if (copies.length <= 1) {
      return
    }

    e.preventDefault()
    const pastedText = e.clipboardData.getData("text")
    handleBulkPaste(adId, "description", pastedText)
  }

  const handleCopyChange = (adId, field, value, index) => {
    setEditedCopies((prev) => {
      const key = `${adId}-${index}`
      return {
        ...prev,
        [key]: {
          ...prev[key],
          adId,
          [field]: value,
          index,
        },
      }
    })
  }

  const handleMainDescriptionChange = (adId, value) => {
    setEditedCopies((prev) => {
      const existingKey = Object.keys(prev).find(
        (key) =>
          key.startsWith(`${adId}-`) && prev[key].mainDescription !== undefined
      )

      const newState = { ...prev }

      if (existingKey) {
        const { mainDescription, ...rest } = newState[existingKey]
        newState[existingKey] = rest
      }

      const targetKey = `${adId}-0`
      return {
        ...newState,
        [targetKey]: {
          ...newState[targetKey],
          adId,
          mainDescription: value,
          index: 0,
        },
      }
    })
  }

  const handleApplyToAll = (adId, field, index) => {
    const key = `${adId}-${index}`

    const hasBeenEdited =
      editedCopies[key] && editedCopies[key][field] !== undefined

    if (!hasBeenEdited) {
      return
    }

    const valueToApply = editedCopies[key][field]

    const newEditedCopies = { ...editedCopies }

    // Sort the ads for consistent ordering when applying to all
    const sortedAds = [...ads].sort((a, b) => a._id.localeCompare(b._id))

    sortedAds?.forEach((ad) => {
      if (ad._id === adId) return

      const adCopies =
        adCopiesState?.filter((copy) => copy.adId?._id === ad._id) || []

      // Sort copies for consistent ordering
      const sortedCopies = [...adCopies].sort((a, b) => {
        if (a._id && b._id) {
          return a._id.localeCompare(b._id)
        }
        return 0
      })

      if (field === "mainDescription") {
        const targetKey = `${ad._id}-0`
        newEditedCopies[targetKey] = {
          ...(newEditedCopies[targetKey] || {}),
          mainDescription: valueToApply,
          adId: ad._id,
          index: 0,
        }
      } else if (index < sortedCopies.length) {
        const targetKey = `${ad._id}-${index}`

        const currentCopy = sortedCopies[index]

        newEditedCopies[targetKey] = {
          ...currentCopy,
          ...(newEditedCopies[targetKey] || {}),
          [field]: valueToApply,
          adId: ad._id,
          index,
        }
      }
    })

    setEditedCopies(newEditedCopies)
    notify("Applied to all ads", "success")
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Use sorted ads to maintain consistent order
      const sortedAds = [...ads].sort((a, b) => a._id.localeCompare(b._id))

      const allCopies = sortedAds.reduce((acc, ad) => {
        // Sort ad copies for consistent ordering
        const adCopies = adCopiesState
          ?.filter((c) => c.adId?._id === ad._id)
          ?.slice()
          ?.sort((a, b) => {
            if (a._id && b._id) {
              return a._id.localeCompare(b._id)
            }
            return 0
          })

        if (ad.creatives.length > 0) {
          acc[ad._id] = {
            adId: ad._id,
            mainDescription: adCopies?.[0]?.mainDescription || "",
            copies: adCopies?.map((copy) => ({
              headline: copy?.headline || "",
              description: copy?.description || "",
            })) || [{ headline: "", description: "" }],
          }
        }
        return acc
      }, {})
      Object.entries(editedCopies).forEach(([key, copy]) => {
        const [adId, index] = key.split("-")
        if (!allCopies[adId]) return

        if (copy.mainDescription !== undefined) {
          allCopies[adId].mainDescription = copy.mainDescription
        }

        if (!allCopies[adId].copies[index]) {
          allCopies[adId].copies[index] = { headline: "", description: "" }
        }

        allCopies[adId].copies[index] = {
          headline:
            copy.headline !== undefined
              ? copy.headline
              : allCopies[adId].copies[index]?.headline || "",
          description:
            copy.description !== undefined
              ? copy.description
              : allCopies[adId].copies[index]?.description || "",
        }
      })

      const apiPayload = Object.values(allCopies)

      const hasEmptyFields = apiPayload.some(
        (item) =>
          !item.copies.some(
            (copy) => copy.headline.trim() && copy.description.trim()
          )
      )

      if (hasEmptyFields) {
        notify("Some ads have empty headlines or descriptions.", "warning")
      }

      await handleUpdateAdCopy(apiPayload)

      const advancedGroupMapping = localStorage.getItem("advancedGroupMapping")

      if (advancedGroupMapping === "true") {
        navigate("/advanced-adset-mapping")
        return
      }

      const campaignObjective = localStorage.getItem("campaignObjective")
      if (campaignObjective === "OUTCOME_APP_PROMOTION") {
        navigate("/review-default-values-ap")
      } else {
        navigate("/review-default-values")
      }
    } catch (error) {
      console.error("Failed to save ad copies:", error)
      alert(error.response.data.message)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (userId && defaultValues?.creativeLocationId) {
      setIsLoading(true)
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId)
      setIsLoading(false)
    }
  }, [userId, defaultValues])

  useEffect(() => {
    if (userId) {
      setIsLoading(true)
      handleGetAdCopies(userId)
      setIsLoading(false)
    }
  }, [userId])

  useEffect(() => {
    if (userId && storedAdAccountId) {
      setIsLoading(true)
      handleGetDefaultValues(userId, storedAdAccountId)
      setIsLoading(false)
    }
  }, [userId, storedAdAccountId])

  useEffect(() => {
    const textareas = document.querySelectorAll("textarea")
    textareas.forEach((textarea) => {
      textarea.style.height = "auto"
      textarea.style.height = `${textarea.scrollHeight}px`
    })
  }, [editedCopies, adCopiesState])

  const getAdCopyForCreative = (adId) => {
    const adCopies = adCopiesState?.filter((copy) => copy.adId?._id === adId)

    const sortedCopies = adCopies?.slice()?.sort((a, b) => {
      if (a._id && b._id) {
        return a._id.localeCompare(b._id)
      }
      return 0
    })

    return sortedCopies?.map((copy, index) => ({
      headline: copy.headline,
      description: copy.description,
      id: copy._id || `${adId}-${index}`,
    }))
  }

  const getMainDescription = (adId) => {
    const editedCopy = Object.entries(editedCopies).find(
      ([key, copy]) =>
        key.startsWith(`${adId}-`) && copy.mainDescription !== undefined
    )

    if (editedCopy) return editedCopy[1].mainDescription

    const adCopy = adCopiesState?.find((copy) => copy.adId?._id === adId)
    return adCopy?.mainDescription || ""
  }

  const isDuplicate = (adId, field, value, currentIndex) => {
    if (!value.trim()) return false

    const copies = getAdCopyForCreative(adId)
    if (!copies || copies.length <= 1) return false

    const currentValue = value.trim()

    for (let i = 0; i < copies.length; i++) {
      if (i === currentIndex) continue

      const compareValue =
        (field === "headline"
          ? editedCopies[`${adId}-${i}`]?.headline !== undefined
            ? editedCopies[`${adId}-${i}`].headline
            : copies[i].headline
          : editedCopies[`${adId}-${i}`]?.description !== undefined
            ? editedCopies[`${adId}-${i}`].description
            : copies[i].description) || ""

      if (compareValue.trim() === currentValue) {
        return true
      }
    }

    return false
  }

  const handleCopyAd = (adId) => {
    const copies = getAdCopyForCreative(adId)
    if (!copies || copies.length === 0) {
      notify("No ad copies found to copy", "error")
      return
    }

    const copiedData = copies.map((copy, index) => {
      const headlineValue =
        editedCopies[`${adId}-${index}`]?.headline !== undefined
          ? editedCopies[`${adId}-${index}`].headline
          : copy.headline

      const descriptionValue =
        editedCopies[`${adId}-${index}`]?.description !== undefined
          ? editedCopies[`${adId}-${index}`].description
          : copy.description

      return {
        headline: headlineValue || "",
        description: descriptionValue || "",
      }
    })

    const mainDescriptionValue = getMainDescription(adId)

    setCopiedAdData({
      copies: copiedData,
      mainDescription: mainDescriptionValue || "",
    })
    notify("Ad copies copied", "success")
  }

  const handlePasteAd = (adId) => {
    if (!copiedAdData || (!copiedAdData.copies && copiedAdData.length === 0)) {
      notify("No ad data to paste", "error")
      return
    }

    const targetCopies = getAdCopyForCreative(adId)
    if (!targetCopies || targetCopies.length === 0) {
      notify("No target ad copies found", "error")
      return
    }

    const copiesToPaste = copiedAdData.copies || copiedAdData
    const mainDescToPaste = copiedAdData.mainDescription

    const maxSlots = Math.min(copiesToPaste.length, targetCopies.length)

    setEditedCopies((prev) => {
      const newState = { ...prev }

      for (let i = 0; i < maxSlots; i++) {
        const key = `${adId}-${i}`
        newState[key] = {
          ...newState[key],
          adId,
          headline: copiesToPaste[i].headline,
          description: copiesToPaste[i].description,
          index: i,
        }
      }

      if (mainDescToPaste !== undefined) {
        const targetKey = `${adId}-0`
        newState[targetKey] = {
          ...newState[targetKey],
          adId,
          mainDescription: mainDescToPaste,
          index: 0,
        }
      }

      return newState
    })

    notify("Ad copies pasted", "success")
  }

  const handleOpenSaveTemplate = (adId) => {
    const copies = getAdCopyForCreative(adId)
    if (!copies || copies.length === 0) {
      notify("No ad copies found to save", "error")
      return
    }
    setAdForSaveTemplate(adId)
    setSaveTemplateName("")
    setSaveTemplateModalOpen(true)
  }

  const handleSaveTemplate = async () => {
    const name = saveTemplateName.trim()
    if (!name) {
      notify("Template name cannot be empty", "error")
      return
    }
    if (!adForSaveTemplate) {
      notify("No ad selected to save", "error")
      return
    }

    const copies = getAdCopyForCreative(adForSaveTemplate) || []

    const headlines = []
    const primaryTexts = []
    copies.forEach((copy, index) => {
      const key = `${adForSaveTemplate}-${index}`
      const headline =
        editedCopies[key]?.headline !== undefined
          ? editedCopies[key].headline
          : copy.headline
      const description =
        editedCopies[key]?.description !== undefined
          ? editedCopies[key].description
          : copy.description

      if (headline && headline.trim()) headlines.push(headline.trim())
      if (description && description.trim()) primaryTexts.push(description.trim())
    })

    if (headlines.length === 0 || primaryTexts.length === 0) {
      notify("Add at least one headline and one primary text before saving", "warning")
      return
    }

    if (new Set(headlines).size !== headlines.length) {
      notify("All headlines must be unique", "error")
      return
    }
    if (new Set(primaryTexts).size !== primaryTexts.length) {
      notify("All primary texts must be unique", "error")
      return
    }

    setIsSavingTemplate(true)
    try {
      const res = await saveAdTemplate({
        user_id: userId,
        template_name: name,
        headlines,
        primary_texts: primaryTexts,
        main_descriptions: [getMainDescription(adForSaveTemplate) || ""],
        fb_ad_account_id: storedAdAccountId,
      })

      if (res?.data?.success === true) {
        notify("Template saved successfully!", "success")
        setSaveTemplateModalOpen(false)
        setSaveTemplateName("")
        setAdForSaveTemplate(null)
      } else {
        notify("Failed to save template!", "error")
      }
    } catch (error) {
      console.error("Failed to save template:", error)
      notify("Failed to save template!", "error")
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const getSortedAds = () => {
    if (!ads) return []
    return [...ads].sort((a, b) => {
      return a._id.localeCompare(b._id)
    })
  }

  const sortedAds = getSortedAds()

  return (
    <div className="max-w-6xl mx-auto flex flex-col gap-4 p-6 bg-white min-h-screen">
      <Navbar
        title="Review Ad Copies"
        onSaveAndNext={handleSave}
        isSaving={isSaving}
        showNext={true}
        adAccountName={storedAdAccountName}
        showLoadAdCopy={false}
        onLoadAdCopy={handleLoadAdSetupClick}
      />

      {(isLoading || isSaving) && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <Loader />
        </div>
      )}

      <div className="space-y-6">
        {sortedAds.map((ad) => {
          return ad.creatives.length > 0 ? (
            <div
              key={ad?._id}
              className="space-y-4 py-6 px-8 bg-white border border-gray-200 rounded-lg shadow-sm flex"
            >
              <div>
                {ad.creatives?.map((creative) => (
                  <div
                    key={creative?._id}
                    className="py-6 px-8 flex flex-col gap-4"
                  >
                    <div className="flex gap-4">
                      <div
                        className="flex items-center gap-4 min-w-[300px] relative"
                        onMouseEnter={() => setPreviewImage(creative)}
                        onMouseLeave={() => setPreviewImage(null)}
                      >
                        <img
                          src={
                            creative.driveThumbnail ||
                            `https://drive.google.com/thumbnail?sz=w456-h200&id=${extractDriveId(
                              creative.fileLink
                            )}`
                          }
                          alt={creative.fileName}
                          className="w-28 h-28 object-cover rounded"
                        />
                        <div>
                          <div className="font-medium text-sm max-w-[150px] break-words">
                            {creative.fileName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {creative.resolution}
                          </div>
                        </div>

                        {previewImage?._id === creative._id && (
                          <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 z-50">
                            <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-2">
                              {creative.fileType === "video/mp4" ? (
                                <video
                                  src={creative?.previewLink}
                                  className="max-w-[500px] max-h-[300px] object-contain rounded"
                                  autoPlay
                                  muted
                                  loop
                                />
                              ) : (
                                <img
                                  src={creative?.previewLink}
                                  alt={creative.fileName}
                                  className="max-w-[500px] max-h-[300px] object-contain rounded"
                                />
                              )}
                              <div className="mt-2 text-xs text-gray-500 text-center">
                                {creative.resolution}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 w-full">
                <div className="flex items-center justify-end w-full gap-2">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleCopyAd(ad._id)}
                    startIcon={<Copy size={12} />}
                  >
                    Copy
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handlePasteAd(ad._id)}
                    startIcon={<Clipboard size={12} />}
                  >
                    Paste
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenSaveTemplate(ad._id)}
                    startIcon={<Save size={12} />}
                  >
                    Save as Template
                  </Button>
                  <button
                      className="flex items-center px-3 py-2 transition-colors border rounded-lg hover:bg-gray-50"
                      style={{
                        borderColor: '#666b73ff',
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderRadius: 8,
                        background: 'white'
                      }}
                      onClick={() => handleLoadAdSetupClick(ad._id)}
                    >
                      <img src="/template.svg" alt="Load Ad Setup" style={{ width: 16, height: 16, marginRight: 8 }} />
                      <span style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: 12,
                        lineHeight: '140%',
                        color: '#565656',
                        whiteSpace: 'nowrap'
                      }}>
                        Load from Templates
                      </span>
                    </button>
                </div>
                {getAdCopyForCreative(ad?._id)?.map((copy, index) => (
                  <div key={copy.id} className="grid grid-cols-2 gap-10">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">
                        Headline {index + 1}
                      </label>
                      <div className="flex flex-col gap-2">
                        {(() => {
                          const headlineValue =
                            editedCopies[`${ad?._id}-${index}`]?.headline !==
                              undefined
                              ? editedCopies[`${ad?._id}-${index}`].headline
                              : copy.headline

                          const isDuplicateHeadline = isDuplicate(
                            ad?._id,
                            "headline",
                            headlineValue,
                            index
                          )

                          return (
                            <>
                              <textarea
                                type="text"
                                className={`w-full px-3 py-2 border ${isDuplicateHeadline
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                                  } rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden h-auto`}
                                placeholder="Enter headline"
                                value={headlineValue}
                                onChange={(e) =>
                                  handleCopyChange(
                                    ad?._id,
                                    "headline",
                                    e.target.value,
                                    index
                                  )
                                }
                                onPaste={(e) =>
                                  handleHeadlinePaste(ad?._id, index, e)
                                }
                              />
                              {isDuplicateHeadline && (
                                <div className="text-xs text-red-600 mt-1">
                                  Duplicate headline detected. Each headline
                                  must be unique.
                                </div>
                              )}
                            </>
                          )
                        })()}
                        {ads?.indexOf(ad) === 0 && (
                          <button
                            onClick={() =>
                              handleApplyToAll(ad?._id, "headline", index)
                            }
                            className="self-start flex items-center justify-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200 py-1 px-2 rounded-md font-medium border border-blue-200"
                          >
                            <Copy size={11} />
                            Apply to all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">
                        Primary Text {index + 1}
                      </label>
                      <div className="flex flex-col gap-2">
                        {(() => {
                          const descriptionValue =
                            editedCopies[`${ad?._id}-${index}`]?.description !==
                              undefined
                              ? editedCopies[`${ad?._id}-${index}`].description
                              : copy.description

                          const isDuplicateDescription = isDuplicate(
                            ad?._id,
                            "description",
                            descriptionValue,
                            index
                          )

                          return (
                            <>
                              <textarea
                                className={`w-full px-3 py-2 border ${isDuplicateDescription
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                                  } rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden h-auto`}
                                placeholder="Enter primary text"
                                value={descriptionValue}
                                onChange={(e) =>
                                  handleCopyChange(
                                    ad?._id,
                                    "description",
                                    e.target.value,
                                    index
                                  )
                                }
                                onPaste={(e) =>
                                  handlePrimaryTextPaste(ad?._id, index, e)
                                }
                              />
                              {isDuplicateDescription && (
                                <div className="text-xs text-red-600 mt-1">
                                  Duplicate primary text detected. Each primary
                                  text must be unique.
                                </div>
                              )}
                            </>
                          )
                        })()}
                        {ads?.indexOf(ad) === 0 && (
                          <button
                            onClick={() =>
                              handleApplyToAll(ad?._id, "description", index)
                            }
                            className="self-start flex items-center justify-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200 py-1 px-2 rounded-md font-medium border border-blue-200"
                          >
                            <Copy size={11} />
                            Apply to all
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Description (Optional)
                  </label>
                  <div className="flex flex-col gap-2">
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-hidden h-auto"
                      placeholder="Enter description"
                      value={getMainDescription(ad?._id)}
                      onChange={(e) =>
                        handleMainDescriptionChange(ad?._id, e.target.value)
                      }
                    />
                    {ads?.indexOf(ad) === 0 && (
                      <button
                        onClick={() => {
                          const currentValue = getMainDescription(ad?._id)
                          if (currentValue) {
                            handleApplyToAll(ad?._id, "mainDescription", 0)
                          }
                        }}
                        className="self-start flex items-center justify-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200 py-1 px-2 rounded-md font-medium border border-blue-200"
                      >
                        <Copy size={11} />
                        Apply to all
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null
        })}
      </div>
      {/* Ad Templates Modal */}
      <Modal
        open={adTemplatesModalOpen}
        onClose={() => {
          setAdTemplatesModalOpen(false)
          setTemplateSearchQuery("")
        }}
        aria-labelledby="ad-templates-modal"
        aria-describedby="ad-templates-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: '800px',
            maxHeight: '80%',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            boxShadow: 24,
            p: 4,
            overflow: 'auto'
          }}
        >
          <div className="flex items-center justify-start mb-4">
            <h2 className="flex-1 font-medium text-xl leading-[140%] text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
              Ad Templates
              {templateSearchQuery.trim()
                ? ` (${filteredAdTemplates.length} of ${adTemplates.length})`
                : ` (${adTemplates.length})`}
            </h2>
            <button
              onClick={() => {
                setAdTemplatesModalOpen(false)
                setTemplateSearchQuery("")
              }}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <img src="/close.svg" alt="Close" width={16} height={16} />
            </button>
          </div>
          {adTemplates.length > 0 && (
            <input
              type="search"
              value={templateSearchQuery}
              onChange={(e) => setTemplateSearchQuery(e.target.value)}
              placeholder="Search by name, headline, primary text, or creator..."
              className="w-full mb-4 px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
          )}
          <div className="grid grid-cols-1 gap-4">
            {filteredAdTemplates.map((template) => {
              const primaryTexts =
                template.primary_texts || template.primaryTexts
              const creatorName = template.createdBy?.name

              return (
                <div
                  key={template._id}
                  className="overflow-hidden hover:border-gray-300 hover:shadow-sm"
                  style={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '12px',
                    boxShadow: '0px 1px 2px 0px #0000000D'
                  }}
                >
                  <div className="px-4 py-3 mb-4 border-b border-[#E5E7EB] h-[42px] bg-[#F9FAFB]">
                  <h4 className="font-medium text-base leading-[140%] text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {template.templateName}
                    </h4>
                  </div>
                  <div className="px-4 mt-3 mb-4">
                    <h4
                      className="mb-2 text-sm font-medium leading-5 text-gray-500"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Headlines
                    </h4>
                    <div
                      className="mb-2 font-medium text-base leading-[140%] text-gray-900 tracking-normal"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {template.template_name}
                    </div>
                    {template.headlines && template.headlines.length > 0 ? (
                      <div>
                        <div className="flex items-center mb-1">
                          <span
                            className="font-normal text-base leading-[140%] text-gray-700"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            1. {template.headlines[0]}
                          </span>
                          {template.headlines.length > 1 && (
                            <span
                              className="flex items-center justify-center whitespace-nowrap rounded-md bg-gray-100 opacity-100 min-w-[38px] h-6 px-3 py-1 gap-1 font-medium text-xs leading-4 text-center ml-2"
                              style={{ fontFamily: "Inter, sans-serif" }}
                            >
                              +{template.headlines.length - 1}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">No headlines</div>
                    )}
                  </div>

                  <div className="px-4">
                    <h4
                      className="mb-2 text-sm font-medium leading-5 text-gray-500"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      Primary Texts
                    </h4>
                    {primaryTexts && primaryTexts.length > 0 ? (
                      <div>
                        <div className="flex items-center mb-1">
                          <span
                            className="truncate font-normal text-base leading-[140%] text-gray-700"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            1. {primaryTexts[0]}
                          </span>
                          {primaryTexts.length > 1 && (
                            <span
                              className="flex items-center justify-center whitespace-nowrap rounded-md bg-gray-100 opacity-100 min-w-[38px] h-6 px-3 py-1 gap-1 font-medium text-xs leading-4 text-center ml-2"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              +{primaryTexts.length - 1}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">No primary texts</div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 px-4 mt-4 mb-4">
                    <span
                      className="text-sm tracking-normal font-medium italic leading-[140%] text-gray-600 truncate"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {creatorName
                        ? `Created by ${creatorName}`
                        : "Created by Unknown"}
                    </span> 
                  <button
                    onClick={() => {
                      // Apply template data to the selected ad
                      const templateHeadlines = template.headlines || [];
                      const templatePrimaryTexts = template.primary_texts || template.primaryTexts || [];

                      if (templateHeadlines.length === 0 && templatePrimaryTexts.length === 0) {
                        notify("Template has no data to apply", "warning");
                        return;
                      }

                      if (!selectedAdForTemplate) {
                        notify("No ad selected to apply template", "error");
                        return;
                      }

                      setEditedCopies((prev) => {
                        const newState = { ...prev };

                        // Apply to the selected ad
                        const selectedAd = sortedAds.find(ad => ad._id === selectedAdForTemplate);
                        if (!selectedAd) {
                          notify("Selected ad not found", "error");
                          return prev;
                        }
                        
                        const adCopies = adCopiesState?.filter((copy) => copy.adId?._id === selectedAdForTemplate) || [];
                        const maxSlots = Math.max(templateHeadlines.length, templatePrimaryTexts.length, adCopies.length);

                        // Apply headlines and primary texts to each copy slot
                        for (let i = 0; i < maxSlots && i < adCopies.length; i++) {
                          const key = `${selectedAdForTemplate}-${i}`;
                          newState[key] = {
                            ...newState[key],
                            adId: selectedAdForTemplate,
                            index: i,
                          };

                          // Apply headline if available
                          if (i < templateHeadlines.length) {
                            newState[key].headline = templateHeadlines[i];
                          }

                          // Apply primary text if available
                          if (i < templatePrimaryTexts.length) {
                            newState[key].description = templatePrimaryTexts[i];
                          }
                        }

                        return newState;
                      });

                      setAdTemplatesModalOpen(false);
                      notify(`Template applied to selected ad successfully!`, "success");
                    }}
                    className="w-[138px] h-8 rounded-lg opacity-100 px-[10px] py-[6px] bg-violet-500 border-0 cursor-pointer font-medium text-sm leading-[140%] text-center text-white"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Choose Template
                  </button>
                </div>
              </div>
            );
          })}
          </div>
          {adTemplates.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              <p>
                No ad templates found. Create templates in the Ad Templates
                page.
              </p>
            </div>
          )}
          {adTemplates.length > 0 && filteredAdTemplates.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              <p>No templates match your search.</p>
            </div>
          )}
        </Box>
      </Modal>

      {/* Save as Template Modal */}
      <Modal
        open={saveTemplateModalOpen}
        onClose={() => {
          if (isSavingTemplate) return
          setSaveTemplateModalOpen(false)
          setSaveTemplateName("")
        }}
        aria-labelledby="save-template-modal"
        aria-describedby="save-template-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "480px",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2
              className="font-medium text-xl leading-[140%]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Save as Template
            </h2>
            <button
              onClick={() => {
                if (isSavingTemplate) return
                setSaveTemplateModalOpen(false)
                setSaveTemplateName("")
              }}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <img src="/close.svg" alt="Close" width={16} height={16} />
            </button>
          </div>
          <p className="mb-2 text-sm text-gray-600" style={{ fontFamily: "Inter, sans-serif" }}>
            The current headlines and primary texts for this ad will be saved as a
            reusable template.
          </p>
          <input
            type="text"
            autoFocus
            value={saveTemplateName}
            onChange={(e) => setSaveTemplateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isSavingTemplate) handleSaveTemplate()
            }}
            placeholder="Enter template name"
            className="w-full mb-4 px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outlined"
              size="small"
              disabled={isSavingTemplate}
              onClick={() => {
                setSaveTemplateModalOpen(false)
                setSaveTemplateName("")
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={isSavingTemplate || !saveTemplateName.trim()}
              onClick={handleSaveTemplate}
            >
              {isSavingTemplate ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  )
}

export default AdCopyPage
